import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Box,
  Typography,
  Avatar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
//import CancelIcon from '@mui/icons-material/Cancel';
//import BuildIcon from '@mui/icons-material/Build';
import { MdOutlineRoomPreferences } from 'react-icons/md';

import { MdNoMeetingRoom } from 'react-icons/md';

const apiUrl = process.env.REACT_APP_API_URL;

const StatCard = ({ title, value, icon, color }) => {
  return (
    <Paper
      elevation={3}
      sx={{
        width: 200,
        padding: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderRadius: 2,
      }}
    >
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {value}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {title}
        </Typography>
      </Box>
      <Avatar
        sx={{ bgcolor: `${color}.main`, color: 'white', width: 40, height: 40 }}
      >
        {icon}
      </Avatar>
    </Paper>
  );
};

const AccommodationList = () => {
  const { t } = useTranslation();
  const [accommodations, setAccommodations] = useState([]);
  const [horses, setHorses] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [editAccommodationId, setEditAccommodationId] = useState(null);
  const [startDate, setStartDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState('');
  const [horseId, setHorseId] = useState('');
  const [roomId, setRoomId] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    fetchAccommodations();
    fetchHorses();
    fetchRooms();
  }, []);

  const fetchAccommodations = () => {
    axios
      .get(`${apiUrl}/accommodations`)
      .then((res) => setAccommodations(res.data))
      .catch((err) => console.error('Error fetching accommodations:', err));
  };

  const fetchHorses = () => {
    axios
      .get(`${apiUrl}/horses`)
      .then((res) => setHorses(res.data))
      .catch((err) => console.error('Error fetching horses:', err));
  };

  const fetchRooms = () => {
    axios
      .get(`${apiUrl}/api/rooms-with-stable-count`)
      .then((res) => {
        const roomsWithDisplayName = res.data.map((room) => ({
          ...room,
          displayName: room.room_name
            ? `${room.stable_name} - ${room.room_name}`
            : 'Room Name Not Available',
        }));
        setRooms(roomsWithDisplayName);
      })
      .catch((err) => console.error('Error fetching rooms:', err));
  };

  const getHorseAgeInMonths = (dob) => {
    const birth = new Date(dob);
    const now = new Date();
    return (
      (now.getFullYear() - birth.getFullYear()) * 12 +
      now.getMonth() -
      birth.getMonth()
    );
  };

  const checkForExistingRoom = () => {
    return accommodations.some(
      (acc) =>
        acc.horse_id === horseId && acc.accommodation_id !== editAccommodationId
    );
  };

  const checkRoomConditions = async () => {
    try {
      const response = await axios.get(`${apiUrl}/accommodations`);
      const roomAccommodations = response.data.filter(
        (acc) => acc.room_id === roomId
      );
      if (roomAccommodations.length === 0) return true;

      const currentHorse = horses.find((h) => h.horse_id === horseId);
      const currentHorseAge = getHorseAgeInMonths(currentHorse.date_of_birth);

      return roomAccommodations.every((acc) => {
        const otherHorse = horses.find((h) => h.horse_id === acc.horse_id);
        const otherHorseAge = getHorseAgeInMonths(otherHorse.date_of_birth);
        return (
          (currentHorseAge < 5 && otherHorse.gender === 'female') ||
          (otherHorseAge < 5 && currentHorse.gender === 'female')
        );
      });
    } catch (err) {
      console.error('Error checking room conditions:', err);
      return false;
    }
  };

  const handleAddOrEdit = async () => {
    if (checkForExistingRoom()) {
      setErrorMessage(t('accommodationList.conflictExistingRoom'));
      setErrorOpen(true);
      return;
    }
    const roomConditionsMet = await checkRoomConditions();
    if (!roomConditionsMet) {
      setErrorMessage(t('accommodationList.conflictRoomConditions'));
      setErrorOpen(true);
      return;
    }

    const data = {
      horse_id: horseId,
      room_id: roomId,
      start_date: startDate
        ? new Date(startDate).toISOString().split('T')[0]
        : null,
      end_date: endDate ? new Date(endDate).toISOString().split('T')[0] : null,
    };

    const request = editAccommodationId
      ? axios.put(`${apiUrl}/accommodations/${editAccommodationId}`, data)
      : axios.post(`${apiUrl}/accommodations`, data);

    request
      .then(() => {
        fetchAccommodations();
        resetForm();
      })
      .catch((err) => console.error('Error saving accommodation:', err));
  };

  const handleDelete = (id) => {
    axios
      .delete(`${apiUrl}/accommodations/${id}`)
      .then(fetchAccommodations)
      .catch((err) => console.error('Error deleting accommodation:', err));
  };

  const openEditDialog = (accommodation) => {
    setEditAccommodationId(accommodation.accommodation_id);
    setHorseId(accommodation.horse_id);
    setRoomId(accommodation.room_id);
    setStartDate(
      accommodation.start_date
        ? new Date(accommodation.start_date).toISOString().split('T')[0]
        : ''
    );
    setEndDate(
      accommodation.end_date
        ? new Date(accommodation.end_date).toISOString().split('T')[0]
        : ''
    );
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditAccommodationId(null);
    setHorseId('');
    setRoomId('');
    setStartDate(new Date().toISOString().split('T')[0]);
    setEndDate('');
    setIsDialogOpen(false);
  };

  const handleErrorClose = () => {
    setErrorOpen(false);
    setErrorMessage('');
  };

  return (
    <Container>
      <Typography variant="h4" align="center" sx={{ mt: 4, mb: 2 }}>
        {t('accommodationList.title')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          justifyContent: 'center',
          mb: 3,
        }}
      >
        <StatCard
          title={t('accommodationList.totalRooms')}
          value={rooms.length}
          icon={<MeetingRoomIcon />}
          color="primary"
        />

        <StatCard
          title={t('accommodationList.occupiedRooms')}
          value={
            rooms.filter(
              (r) => r.status === 'available' && r.accommodation_count > 0
            ).length
          }
          icon={<MdNoMeetingRoom />}
          color="secondary"
        />
        <StatCard
          title={t('accommodationList.availableRooms')}
          value={
            rooms.filter(
              (r) => r.status === 'available' && r.accommodation_count === 0
            ).length
          }
          icon={<CheckCircleIcon />}
          color="success"
        />
        <StatCard
          title={t('accommodationList.maintenanceRooms')}
          value={rooms.filter((r) => r.status === 'maintenance').length}
          icon={<MdOutlineRoomPreferences />}
          color="warning"
        />
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-start', // محاذاة العناصر لليسار
          alignItems: 'center', // التأكد من أن الزر والنص في نفس الخط
          mb: 2.5,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setIsDialogOpen(true)}
          style={{ marginTop: 0, alignSelf: 'center' }} // تعديل المحاذاة
        >
          {t('accommodationList.addNew')}
        </Button>
      </Box>
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center">
                {t('accommodationList.horseName')}
              </TableCell>
              <TableCell align="center">
                {t('accommodationList.stableRoomName')}
              </TableCell>
              <TableCell align="center">
                {t('accommodationList.capacity')}
              </TableCell>
              <TableCell align="center">
                {t('accommodationList.startDate')}
              </TableCell>
              <TableCell align="center">
                {t('accommodationList.endDate')}
              </TableCell>
              <TableCell align="center">
                {t('accommodationList.actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {accommodations.length > 0 ? (
              accommodations.map((acc) => (
                <TableRow key={acc.accommodation_id}>
                  <TableCell align="center">{acc.horse_name}</TableCell>
                  <TableCell align="center">
                    {acc.stable_name && acc.room_name
                      ? `${acc.stable_name} - ${acc.room_name}`
                      : 'Room Name Not Available'}
                  </TableCell>
                  <TableCell align="center">{acc.capacity}</TableCell>
                  <TableCell align="center">
                    {new Date(acc.start_date).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="center">
                    {acc.end_date
                      ? new Date(acc.end_date).toLocaleDateString()
                      : 'N/A'}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="primary"
                      onClick={() => openEditDialog(acc)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="secondary"
                      onClick={() => handleDelete(acc.accommodation_id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {t('accommodationList.noAccommodations')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={isDialogOpen} onClose={resetForm} fullWidth maxWidth="sm">
        <DialogTitle>
          {editAccommodationId
            ? t('accommodationList.editAccommodation')
            : t('accommodationList.addAccommodation')}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('accommodationList.horseName')}</InputLabel>
            <Select
              value={horseId}
              onChange={(e) => setHorseId(e.target.value)}
            >
              {horses.map((h) => (
                <MenuItem key={h.horse_id} value={h.horse_id}>
                  {h.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('accommodationList.stableRoomName')}</InputLabel>
            <Select value={roomId} onChange={(e) => setRoomId(e.target.value)}>
              {rooms.map((room) => (
                <MenuItem key={room.room_id} value={room.room_id}>
                  {room.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label={t('accommodationList.startDate')}
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label={t('accommodationList.endDate')}
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={resetForm}>{t('accommodationList.cancel')}</Button>
          <Button onClick={handleAddOrEdit} color="secondary">
            {editAccommodationId
              ? t('accommodationList.update')
              : t('accommodationList.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={errorOpen}
        autoHideDuration={6000}
        onClose={handleErrorClose}
      >
        <Alert
          onClose={handleErrorClose}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AccommodationList;
